import { useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import type {
	ServiceMutationResult,
	ServiceQueryResult,
} from '@atlassian/jira-software-deployments-service-query-types';
import type { DeploymentEnvironmentType } from '../../common/types';
import { toEnvironmentTypeFilter } from '../../common/utils/environment-type-filter';
import { useGiraClient } from '../../common/utils/graphql-clients';
import type {
	jira_deploymentsPage_getMyPreferences_Query as MyPreferencesQuery,
	jira_deploymentsPage_getMyPreferences_QueryVariables as MyPreferencesQueryVariables,
} from './__generated_apollo__/jira_deploymentsPage_getMyPreferences_Query';
import type {
	jira_deploymentsPage_setMyPreference_Mutation as SetMyPreferenceMutation,
	jira_deploymentsPage_setMyPreference_MutationVariables as SetMyPreferenceMutationVariables,
} from './__generated_apollo__/jira_deploymentsPage_setMyPreference_Mutation';
import { VIEW_CONFIGURATION_PREFERENCE_KEY } from './constants';
import { MY_PREFERENCE_QUERY, SET_MY_PREFERENCE_MUTATION } from './gql';
import type {
	IssuesViewConfiguration,
	ViewConfigurationPreference,
	IconsViewConfiguration,
} from './types';
import { transformEnvironmentViewPreferenceQueryResult } from './utils/transformers';

export const useViewConfigurationPreference =
	(): ServiceQueryResult<ViewConfigurationPreference> => {
		const giraClient = useGiraClient();
		const queryResult = useQuery<MyPreferencesQuery, MyPreferencesQueryVariables>(
			MY_PREFERENCE_QUERY,
			{
				variables: {
					keys: [VIEW_CONFIGURATION_PREFERENCE_KEY],
				},
				client: giraClient,
			},
		);

		return transformEnvironmentViewPreferenceQueryResult(queryResult);
	};

export const useEnvironmentTypesFilter = (): DeploymentEnvironmentType[] | null => {
	const { loading, error, data } = useViewConfigurationPreference();

	if (error) {
		throw error;
	}

	if (loading || data === undefined) {
		return null;
	}

	return toEnvironmentTypeFilter(data.environmentType);
};

export const useIconsConfigurationPreference = (): IconsViewConfiguration | null => {
	const { loading, error, data } = useViewConfigurationPreference();

	if (error) {
		throw error;
	}

	if (loading || data === undefined) {
		return null;
	}

	return data.icons;
};

export const useIssueConfigurationPreference = (): IssuesViewConfiguration | null => {
	const { loading, error, data } = useViewConfigurationPreference();

	if (error) {
		throw error;
	}

	if (loading || data === undefined) {
		return null;
	}

	return data.issues;
};

type SetEnvironmentViewPreferenceFn = (newConfig: ViewConfigurationPreference) => void;
export const useSetViewConfigurationPreference = (): [
	SetEnvironmentViewPreferenceFn,
	ServiceMutationResult<undefined>,
] => {
	const giraClient = useGiraClient();
	const [setMyPreference, { loading, error, called }] = useMutation<
		SetMyPreferenceMutation,
		SetMyPreferenceMutationVariables
	>(SET_MY_PREFERENCE_MUTATION, {
		// @ts-expect-error - TS2322 - Type 'ApolloClient<unknown>' is not assignable to type 'ApolloClient<object>'.
		client: giraClient,
	});
	const setEnvironmentViewPreferenceFn = useCallback(
		(newConfig: ViewConfigurationPreference) => {
			const stringifiedNewConfig = JSON.stringify(newConfig);
			setMyPreference({
				variables: {
					input: { key: VIEW_CONFIGURATION_PREFERENCE_KEY, value: stringifiedNewConfig },
				},
				optimisticResponse: {
					setMyPreference: {
						__typename: 'SetMyPreferenceOutput',
						success: true,
					},
				},
				update: (proxy, mutationResponse) => {
					if (
						mutationResponse.data == null ||
						mutationResponse.data.setMyPreference == null ||
						mutationResponse.data.setMyPreference.success === false
					) {
						// In case mutation fails, returning here will revert optimistic update
						return;
					}
					const newData = { myPreferences: {} };
					// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '"jira.user.deployments.timeline.view.configuration"' can't be used to index type '{}'.
					newData.myPreferences[VIEW_CONFIGURATION_PREFERENCE_KEY] = stringifiedNewConfig;

					proxy.writeQuery<MyPreferencesQuery, MyPreferencesQueryVariables>({
						query: MY_PREFERENCE_QUERY,
						variables: {
							keys: [VIEW_CONFIGURATION_PREFERENCE_KEY],
						},
						// @ts-expect-error - TS2345 - Argument of type '{ query: DocumentNode; variables: { keys: string[]; }; client: ApolloClient<unknown>; data: { myPreferences: {}; }; }' is not assignable to parameter of type 'WriteQueryOptions<jira_deploymentsPage_getMyPreferences_Query, jira_deploymentsPage_getMyPreferences_QueryVariables>'.
						client: giraClient,
						data: newData,
					});
				},
			});
		},
		[giraClient, setMyPreference],
	);

	return [setEnvironmentViewPreferenceFn, { loading, error, called }];
};
